import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { Badge, Avatar, Modal, EmptyState, useToast, Skeleton } from '../ui';
import { useLang } from '../Layout';
import { FaArrowLeft, FaBoxesStacked, FaFileInvoiceDollar, FaUsers, FaMoneyBillWave, FaPhone, FaHandshake } from 'react-icons/fa6';

const TRADES = { plumber: 'প্লাম্বার', electrician: 'ইলেকট্রিশিয়ান', rajmistri: 'রাজমিস্ত্রি', 'road mistri': 'রোড মিস্ত্রি', 'tis mistri': 'টিস মিস্ত্রি', welder: 'ওয়েল্ডার', painter: 'পেইন্টার', other: 'অন্যান্য' };

const T = {
  bn: {
    back: 'ফিরে যান', tabWorkers: 'কর্মী', tabStock: 'মাল', tabMoney: 'টাকা',
    daysWorked: 'দিন কাজ করেছে', todayBadge: 'আজ', workers: 'জন কর্মী',
    billed: 'বিল (পাবে)', paidAmt: 'নিয়েছে', stillDue: 'বাকি', newBill: 'নতুন বিল', billFor: 'বিল (কন্ট্রাক্ট ভিত্তিক)',
    workSummary: 'কাজের বিবরণ', amount: 'বিলের পরিমাণ (৳)', paidNow: 'এখনই কত দেবেন (৳)',
    weekStart: 'সপ্তাহ শুরু (শনিবার)', payNow: 'টাকা দিন', noBills: 'এখনো কোনো বিল নেই। কাজ শেষ হলে বিল যোগ করুন।',
    noStock: 'এই সাইড এখনো কোনো মাল নেয়নি', noWorker: 'এই কন্ট্রাক্টরের আওতায় কোনো কর্মী নেই',
    billAdded: 'বিল যোগ হয়েছে', paidToast: 'পরিশোধ সংরক্ষণ হয়েছে',
    dailyHint: 'দৈনিক ভিত্তিক — কর্মীদের বেতন হাজিরা অনুযায়ী গণনা হয় (বেতন পেজে)',
    loading: 'লোড হচ্ছে…', employee: 'জন কর্মী', empty: 'ডেটা খুঁজে পাওয়া যায়নি', emptyFind: 'ডেটা পাওয়া যায়নি',
  },
  en: {
    back: 'Back', tabWorkers: 'Workers', tabStock: 'Material', tabMoney: 'Money',
    daysWorked: 'days worked', todayBadge: 'Today', workers: 'workers',
    billed: 'Billed (to get)', paidAmt: 'Taken', stillDue: 'Due', newBill: 'New Bill', billFor: 'Bills (contract based)',
    workSummary: 'Work summary', amount: 'Bill amount (৳)', paidNow: 'Pay now (৳)',
    weekStart: 'Week start (Saturday)', payNow: 'Pay now', noBills: 'No bills yet. Add a bill after work is done.',
    noStock: 'This side has not taken any material yet', noWorker: 'No workers under this contractor',
    billAdded: 'Bill added', paidToast: 'Payment saved',
    dailyHint: 'Daily wage — worker salaries are computed from attendance (Salaries page)',
    loading: 'Loading…', missing: 'Data not found',
  },
};

const weekStartOf = (ds) => { const [y, m, d] = ds.split('-').map(Number); const dt = new Date(y, m - 1, d); const diff = (dt.getDay() + 1) % 7; dt.setDate(dt.getDate() - diff); return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`; };
const fmt = (n) => Number(n || 0).toLocaleString('bn-BD');

export default function ContractorDetail({ role }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang(); const t = T[lang];
  const { toast } = useToast();
  const base = role === 'manager' ? '/manager' : '/owner';
  const [d, setD] = useState(null);
  const [tab, setTab] = useState('workers');
  const [showBill, setShowBill] = useState(false);
  const [billForm, setBillForm] = useState({ week_start: weekStartOf(new Date().toISOString().slice(0, 10)), work_summary: '', amount: '', amount_paid: '' });
  const [err, setErr] = useState('');
  const [paying, setPaying] = useState(null);

  const load = () => api.get(`${base}/contractors/${id}/detail`).then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  async function addBill(e) {
    e.preventDefault(); setErr('');
    try {
      await api.post(`${base}/bills`, { contractor_id: d.contractor.id, ...billForm });
      toast(t.billAdded, 'success');
      setShowBill(false); setBillForm({ week_start: weekStartOf(new Date().toISOString().slice(0, 10)), work_summary: '', amount: '', amount_paid: '' });
      load();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  async function payBill(bill) {
    setPaying(bill.id);
    try {
      const pay = prompt(lang === 'bn' ? `কত টাকা দিবেন? (বাকি ${fmt(bill.amount - bill.amount_paid)})` : `How much to pay? (due ${fmt(bill.amount - bill.amount_paid)})`, Math.max(0, bill.amount - bill.amount_paid));
      if (pay === null) return;
      const amt = Number(pay);
      if (isNaN(amt) || amt <= 0) return;
      await api.post(`${base}/bills/${bill.id}/pay`, { amount: amt });
      toast(t.paidToast, 'success');
      load();
    } catch (e) { toast(e.response?.data?.message || 'সমস্যা হয়েছে', 'error'); } finally { setPaying(null); }
  }

  if (!d) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-800"><FaArrowLeft /> {t.back}</button>
        <div className="space-y-3"><Skeleton className="h-24" /><Skeleton className="h-40" /></div>
      </div>
    );
  }

  const ct = d.contractor;
  const isContract = ct.billing === 'contract';

  return (
    <div className="space-y-4">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-ink-500 hover:text-ink-800 transition"><FaArrowLeft /> {t.back}</button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-4 sm:p-5">
        <div className="flex items-center gap-3.5">
          <Avatar name={ct.name} className="w-14 h-14 text-base" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-bold text-ink-900 truncate">{ct.name}</h1>
              {isContract
                ? <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">{lang === 'bn' ? 'কন্ট্রাক্ট ভিত্তিক' : 'Contract'}</span>
                : <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{lang === 'bn' ? 'দৈনিক ভিত্তিক' : 'Daily'}</span>}
              <Badge color={ct.is_active ? 'active' : 'paused'}>{ct.is_active ? (lang === 'bn' ? 'সক্রিয়' : 'Active') : (lang === 'bn' ? 'বন্ধ' : 'Inactive')}</Badge>
            </div>
            <div className="text-sm text-ink-400 mt-1">{TRADES[ct.trade] || ct.trade}{ct.phone && <> • <FaPhone className="inline text-ink-300" size={11} /> {ct.phone}</>}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-4 border-t border-ink-50">
          <div className="bg-ink-50 rounded-xl py-2.5 text-center">
            <div className="text-[10px] text-ink-400">{t.workers}</div>
            <div className="font-bold text-ink-900">{d.workers.length}</div>
          </div>
          <div className="bg-sky-50 rounded-xl py-2.5 text-center">
            <div className="text-[10px] text-ink-400">{t.tabStock}</div>
            <div className="font-bold text-ink-900">{d.stock_totals.length}</div>
          </div>
          <div className="bg-emerald-50 rounded-xl py-2.5 text-center">
            <div className="text-[10px] text-ink-400">{t.paidAmt}</div>
            <div className="font-bold text-emerald-700">৳{fmt(d.bill_totals.paid)}</div>
          </div>
          <div className="bg-amber-50 rounded-xl py-2.5 text-center">
            <div className="text-[10px] text-ink-400">{t.stillDue}</div>
            <div className="font-bold text-amber-700">৳{fmt(d.bill_totals.billed - d.bill_totals.paid)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 bg-white border border-ink-100 rounded-xl p-1">
        {[['workers', t.tabWorkers], ['stock', t.tabStock], ['money', t.tabMoney]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition ${tab === k ? 'bg-ink-900 text-white' : 'text-ink-400 hover:bg-ink-50'}`}>{l}</button>
        ))}
      </div>

      {/* Workers */}
      {tab === 'workers' && (
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
          {d.workers.length === 0 && <EmptyState icon="👷" title={t.noWorker} />}
          {d.workers.map((wk) => (
            <div key={wk.id} className="flex items-center gap-3 px-4 py-3 border-b border-ink-50 last:border-0">
              <Avatar name={wk.name} className="w-9 h-9 text-xs" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink-800 truncate">{wk.name}</div>
                <div className="text-[11px] text-ink-400">
                  {wk.present_days} {t.daysWorked}
                  {wk.today_status && (
                    <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${wk.today_status === 'present' ? 'bg-emerald-50 text-emerald-700' : wk.today_status === 'absent' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-700'}`}>
                      {t.todayBadge}: {wk.today_status}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs font-semibold text-ink-700 shrink-0">৳{wk.rate_per_day}/{lang === 'bn' ? 'দিন' : 'day'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Stock */}
      {tab === 'stock' && (
        <div className="space-y-3">
          {d.stock_totals.length > 0 && (
            <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-4">
              <h3 className="text-xs font-semibold text-ink-400 uppercase mb-2">{lang === 'bn' ? 'মোট নেওয়া মাল' : 'Total material taken'}</h3>
              <div className="flex flex-wrap gap-1.5">
                {d.stock_totals.map((s, i) => (
                  <span key={i} className="text-xs font-semibold bg-sky-50 text-sky-700 px-2.5 py-1 rounded-full">{s.material_name}: {s.qty} {s.unit}</span>
                ))}
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-ink-100 shadow-soft overflow-hidden">
            {d.stock_out.length === 0 && <EmptyState icon="📦" title={t.noStock} />}
            {d.stock_out.slice(0, 50).map((so) => (
              <div key={so.id} className="flex items-center gap-3 px-4 py-3 border-b border-ink-50 last:border-0">
                <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center shrink-0"><FaBoxesStacked size={14} /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-ink-800 truncate">{so.material_name} <span className="text-ink-400 font-normal">× {so.qty} {so.unit}</span></div>
                  <div className="text-[11px] text-ink-400 truncate">{so.work_description || '—'} • {so.created_at?.slice(0, 10)}</div>
                </div>
                <FaHandshake className="text-ink-200 shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Money */}
      {tab === 'money' && (
        <div className="space-y-3">
          {isContract ? (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-xl border border-ink-100 shadow-soft py-3">
                  <div className="text-[10px] text-ink-400">{t.billed}</div>
                  <div className="font-bold text-ink-900 text-sm">৳{fmt(d.bill_totals.billed)}</div>
                </div>
                <div className="bg-emerald-50 rounded-xl py-3">
                  <div className="text-[10px] text-ink-400">{t.paidAmt}</div>
                  <div className="font-bold text-emerald-700 text-sm">৳{fmt(d.bill_totals.paid)}</div>
                </div>
                <div className="bg-amber-50 rounded-xl py-3">
                  <div className="text-[10px] text-ink-400">{t.stillDue}</div>
                  <div className="font-bold text-amber-700 text-sm">৳{fmt(d.bill_totals.billed - d.bill_totals.paid)}</div>
                </div>
              </div>
              <button className="btn-ghost w-full text-sm flex items-center justify-center gap-2" onClick={() => { setShowBill(true); setErr(''); }}>
                <FaFileInvoiceDollar /> {t.newBill}
              </button>
              <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-4">
                <h3 className="font-bold text-ink-900 mb-2">{t.billFor}</h3>
                {d.bills.length === 0 && <div className="text-xs text-ink-400 py-4 text-center">{t.noBills}</div>}
                {d.bills.map((b) => (
                  <div key={b.id} className="border-b border-ink-50 last:border-0 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-ink-800">{b.week_start}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge color={b.status === 'paid' ? 'paid' : b.status === 'partial' ? 'partial' : 'pending'}>
                          {b.status === 'paid' ? (lang === 'bn' ? 'পরিশোধিত' : 'Paid') : b.status === 'partial' ? (lang === 'bn' ? 'আংশিক' : 'Partial') : (lang === 'bn' ? 'বাকি' : 'Due')}
                        </Badge>
                        {b.amount_paid < b.amount && (
                          <button onClick={() => payBill(b)} disabled={paying === b.id} className="text-[11px] font-semibold text-white bg-emerald-500 hover:bg-emerald-600 px-2.5 py-1 rounded-lg disabled:opacity-50">{t.payNow}</button>
                        )}
                      </div>
                    </div>
                    {b.work_summary && <div className="text-xs text-ink-500 mt-1">{b.work_summary}</div>}
                    <div className="text-[11px] text-ink-400 mt-1">৳{fmt(b.amount)} • {t.paidAmt} ৳{fmt(b.amount_paid)}{b.amount_paid < b.amount && ` • ${t.stillDue} ৳${fmt(b.amount - b.amount_paid)}`}</div>
                  </div>
                ))}
              </div>
              <div className="flex items-start gap-2 text-xs text-ink-400 bg-ink-50 rounded-xl p-3">
                <FaMoneyBillWave className="mt-0.5 shrink-0 text-brand-500" />
                {lang === 'bn' ? 'মাল বিতরণের "কাজের বিবরণ" থেকে কাজের হিসাব হয় — প্রতি সপ্তাহে বিল যোগ করুন, তারপর টাকা দিন।' : 'Bills are created weekly based on work done (stock-out work descriptions).'}
              </div>
            </>
          ) : (
            <div className="text-xs text-ink-400 bg-ink-50 rounded-xl p-4">{t.dailyHint}</div>
          )}
        </div>
      )}

      {/* New bill modal */}
      <Modal open={showBill} onClose={() => setShowBill(false)} title={`${t.billFor} — ${ct.name}`}>
        <form onSubmit={addBill} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div><label className="label">{t.weekStart} <span className="text-rose-500">*</span></label>
            <input className="input" type="date" value={billForm.week_start} onChange={(e) => setBillForm({ ...billForm, week_start: e.target.value })} required /></div>
          <div><label className="label">{t.workSummary}</label>
            <textarea className="input" rows="2" placeholder={lang === 'bn' ? 'যেমন: ২য় তলার বাথরুমের স্যানিটারি কাজ…' : 'e.g. 2nd floor bathroom sanitary work…'} value={billForm.work_summary} onChange={(e) => setBillForm({ ...billForm, work_summary: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">{t.amount} <span className="text-rose-500">*</span></label>
              <input className="input" type="number" min="0" value={billForm.amount} onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })} required /></div>
            <div><label className="label">{t.paidNow}</label>
              <input className="input" type="number" min="0" value={billForm.amount_paid} onChange={(e) => setBillForm({ ...billForm, amount_paid: e.target.value })} /></div>
          </div>
          <button className="btn-primary w-full py-3">{t.newBill}</button>
        </form>
      </Modal>
    </div>
  );
}